import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { invalidateHardwareDevice } from '@/app-shell/chronicle/hooks/query/useQueryHardwareDevice';
import { invalidateListHardwareDevices } from '@/app-shell/chronicle/hooks/query/useQueryListHardwareDevices';
import { patchHardwareDevice } from '@/pages/api/scribeAPI';
import type { HardwareDeviceResponse } from '@/types';
import { logger } from '@/utils/logger/logger';

const PATCH_HARDWARE_DEVICE_FIRMWARE = 'patchHardwareDeviceFirmware';

export interface PatchDeviceFirmwareVariables {
  id: string;
  firmwareVersion: string;
}

// Fire-and-forget post-OTA backend sync — failures never block the OTA UX.
export function useMutatePatchDeviceFirmware() {
  return useMutation<HardwareDeviceResponse, unknown, PatchDeviceFirmwareVariables>({
    mutationKey: [PATCH_HARDWARE_DEVICE_FIRMWARE],
    mutationFn: async ({ id, firmwareVersion }) => {
      const { data } = await patchHardwareDevice(id, {
        firmware_version: firmwareVersion,
      });
      return data;
    },
    onSuccess: async (data, variables) => {
      logger.info({
        msg: '[useMutatePatchDeviceFirmware] Patched remote firmware version after OTA',
        id: variables.id,
      });
      // Invalidate both the single-device and list caches — the restore/
      // auto-connect seed hooks read the list, so a stale list would re-seed
      // the old firmware into the atom before BLE reports the booted version.
      await Promise.all([
        invalidateHardwareDevice(variables.id),
        invalidateListHardwareDevices(),
      ]);
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403 || status === 404) {
          logger.warn({
            msg: '[useMutatePatchDeviceFirmware] PATCH rejected; leaving local link intact',
            status,
          });
          return;
        }
      }
      logger.error({
        msg: '[useMutatePatchDeviceFirmware] Failed to PATCH remote firmware version',
        error,
      });
    },
  });
}
